class ProductSlideshow extends HTMLElement {
  constructor() {
    super();

    this.mediaWrapper = this.querySelector(".product-slideshow-slider");
    this.medias = this.querySelectorAll(".product-slideshow-slide");
    this.thumbnailsWrapper = this.querySelector(
      ".product-slideshow-thumbnails",
    );
    this.thumbnails = this.querySelectorAll(".product-slideshow-thumb-wrap");
    this.thumbnailImages = this.querySelectorAll(
      ".product-slideshow-thumb-wrap img",
    );
    this.sliderPrevBtn = this.querySelector('[data-switch-action="prev"]');
    this.sliderNextBtn = this.querySelector('[data-switch-action="next"]');

    this.currentIndex;

    this.setupEvents();
    this.setupVideos();

    this.zoomAction(this.mediaWrapper);
  }

  setupEvents() {
    const mediaScrollObserver = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const newIndex = parseInt(entry.target.dataset.index);
            this.switchThumbnailIndex(newIndex);
            this.visibilityButton(newIndex);
            this.scrollThumbnails(newIndex);
            this.currentIndex = newIndex;
          }
        });
      },
      {
        threshold: 0.5,
      },
    );

    this.medias.forEach((media) => {
      mediaScrollObserver.observe(media);
    });

    this.thumbnails.forEach((thumbnail) => {
      thumbnail.addEventListener("click", () => {
        const newIndex = parseInt(thumbnail.querySelector("img").dataset.index);
        this.switchToIndex(newIndex);
      });
    });

    this.addEventListener("updateImages", (e) => {
      this.updateImages(e.detail);
    });

    this.sliderNextBtn.addEventListener("click", () => {
      this.switchPrevOrNext(false);
    });

    this.sliderNextBtn.addEventListener("keydown", (e) => {
      if (e.key === "Enter") {
        this.switchPrevOrNext(false);
      }
    });

    this.sliderPrevBtn.addEventListener("click", () => {
      this.switchPrevOrNext(true);
    });

    this.sliderPrevBtn.addEventListener("keydown", (e) => {
      if (e.key === "Enter") {
        this.switchPrevOrNext(true);
      }
    });
  }

  setupVideos() {
    const videoSlides = [...this.medias].filter(
      (media) =>
        media.dataset.mediaType === "video" ||
        media.dataset.mediaType === "external_video",
    );
    if (videoSlides.length === 0) return;

    const reduceMotion = window.matchMedia(
      "(prefers-reduced-motion: reduce)",
    ).matches;

    // Uploaded videos play muted while their slide is on screen, like an animated
    // image. External players are removed when their slide leaves the screen.
    const videoObserver = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          const slide = entry.target;

          if (slide.dataset.mediaType === "external_video") {
            if (!entry.isIntersecting) this.unloadExternalVideo(slide);
            return;
          }

          const video = slide.querySelector("video");
          if (!entry.isIntersecting) {
            video.pause();
          } else if (!reduceMotion && slide.dataset.userPaused !== "true") {
            this.playVideo(video);
          }
        });
      },
      {
        threshold: 0.5,
      },
    );

    videoSlides.forEach((slide) => {
      if (slide.dataset.mediaType === "video") {
        this.setupVideoSlide(slide);
      } else {
        this.setupExternalVideoSlide(slide);
      }
      videoObserver.observe(slide);
    });

    this.setupThumbnailVideos(reduceMotion);
  }

  setupThumbnailVideos(reduceMotion) {
    // Thumbnail previews loop on their own, independent of the main slide
    const thumbObserver = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        const wrap = entry.target;
        const video = wrap.querySelector("video");
        if (!entry.isIntersecting) {
          video.pause();
        } else if (!reduceMotion) {
          this.playVideo(video);
        }
      });
    });

    this.querySelectorAll(".product-slideshow-thumb-wrap--video").forEach(
      (wrap) => {
        const video = wrap.querySelector(".product-slideshow-thumb-video");
        if (!video) return;

        video.muted = true;
        video.addEventListener("playing", () => {
          wrap.dataset.playing = "true";
        });

        thumbObserver.observe(wrap);
      },
    );
  }

  setupVideoSlide(slide) {
    const video = slide.querySelector("video");
    const toggle = slide.querySelector("[data-video-toggle]");
    video.muted = true;

    const syncToggle = () => {
      const playing = !video.paused;
      toggle.dataset.state = playing ? "playing" : "paused";
      toggle.setAttribute(
        "aria-label",
        playing ? "Video pausieren" : "Video abspielen",
      );
    };
    video.addEventListener("play", syncToggle);
    video.addEventListener("pause", syncToggle);

    const togglePlayback = (e) => {
      e.stopPropagation();
      if (video.paused) {
        slide.dataset.userPaused = "false";
        this.playVideo(video);
      } else {
        slide.dataset.userPaused = "true";
        video.pause();
      }
    };
    toggle.addEventListener("click", togglePlayback);
    video.addEventListener("click", togglePlayback);
  }

  playVideo(video) {
    // Low power mode on iOS rejects play(); the toggle then stays on "play"
    const playPromise = video.play();
    if (playPromise) playPromise.catch(() => {});
  }

  setupExternalVideoSlide(slide) {
    const wrapper = slide.querySelector(".product-slideshow-video--external");
    const loadButton = slide.querySelector("[data-external-video-load]");

    // The YouTube / Vimeo player is only inserted on click, so no third-party
    // requests or cookies happen before the customer asks for the video
    loadButton.addEventListener("click", (e) => {
      e.stopPropagation();
      if (wrapper.dataset.loaded === "true") return;

      const template = wrapper.querySelector("template");
      wrapper.appendChild(template.content.cloneNode(true));
      wrapper.dataset.loaded = "true";
    });
  }

  unloadExternalVideo(slide) {
    const wrapper = slide.querySelector(".product-slideshow-video--external");
    if (wrapper.dataset.loaded !== "true") return;

    wrapper.querySelector("iframe")?.remove();
    wrapper.dataset.loaded = "false";
  }

  scrollThumbnails(index) {
    return;
    this.thumbnails[index].scrollIntoView({
      behavior: "smooth",
      container: "nearest",
    });
  }

  switchPrevOrNext(prev) {
    // Sometimes there are gaps in the image ids, e.g. when some images are hidden due to the variant images. This is a bypass

    const currentActiveImageIds = [...this.thumbnailImages]
      .filter((thumb) => thumb.dataset.show === "true")
      .map((thumb) => parseInt(thumb.dataset.index));
    let currentIndex = this.currentIndex;
    const indexOfCurrentIndex = currentActiveImageIds.indexOf(currentIndex);
    const targetIndex = prev
      ? indexOfCurrentIndex - 1
      : indexOfCurrentIndex + 1;
    if (targetIndex < 0) {
      return;
    }

    const switchToIndex = currentActiveImageIds[targetIndex];
    if (switchToIndex !== undefined) {
      this.switchToIndex(switchToIndex);
    }
  }

  visibilityButton(currentIndex) {
    let size = this.thumbnails.length - 1;
    currentIndex === 0
      ? this.sliderPrevBtn.setAttribute("data-active", "false")
      : this.sliderPrevBtn.setAttribute("data-active", "true");

    currentIndex === size
      ? this.sliderNextBtn.setAttribute("data-active", "false")
      : this.sliderNextBtn.setAttribute("data-active", "true");
  }

  switchToIndex(index) {
    this.switchThumbnailIndex(index);

    const currentMediaWidth = this.querySelector(
      `.product-slideshow-slide[data-index='${index}']`,
    ).getBoundingClientRect().width;

    const indexWithoutHiddenMedia =
      [...this.medias]
        .filter((media) => {
          return parseInt(media.dataset.index) <= index;
        })
        .filter((media) => {
          return media.dataset.show === "true";
        }).length - 1;
    this.mediaWrapper.scrollLeft = indexWithoutHiddenMedia * currentMediaWidth;
  }

  switchThumbnailIndex(index) {
    this.getCurrentActiveThumbnail().setAttribute("data-active", false);
    this.querySelector(
      `.product-slideshow-thumbnail[data-index='${index}']`,
    ).setAttribute("data-active", true);
  }

  getCurrentActiveThumbnail() {
    return this.querySelector(
      ".product-slideshow-thumbnail[data-active='true']",
    );
  }

  updateImages(variantId) {
    [...this.medias]
      .filter((media) => media.dataset.imageType === "VARIANT")
      .forEach((media) => {
        const showImage = parseInt(media.dataset.imageVariantId) === variantId;
        media.setAttribute("data-show", showImage);
      });
    [...this.thumbnailImages]
      .filter(
        (thumbnail) => thumbnail.getAttribute("data-image-type") === "VARIANT",
      )
      .forEach((thumbnail) => {
        let showThumbnail =
          parseInt(thumbnail.dataset.imageVariantId) === variantId;
        thumbnail.setAttribute("data-show", showThumbnail);
      });
    // Using timeout as the methods from above automatically trigger the IntersectionObserver before
    setTimeout(() => {
      this.mediaWrapper.scrollLeft = 0;
      window.scrollTo(0, 0);
      this.thumbnailsWrapper.scrollTop = 0;
      this.thumbnailsWrapper.scrollLeft = 0;
    }, 50);
  }

  zoomAction(slider) {
    if (window.innerWidth > 1023) {
      [...slider.children]
        .filter((slide) => slide.dataset.mediaType === "image")
        .forEach((slide) => {
          slide.addEventListener("click", (e) => {
            if (!this.stateZoom) {
              this.zoom(e, slide);
              this.stateZoom = true;
            } else {
              this.resetZoom(slide);
            }
          });

          slide.addEventListener("mousemove", (e) => {
            if (this.stateZoom) {
              this.zoom(e, slide);
            }
          });

          slide.addEventListener("mouseleave", () => this.resetZoom(slide));
        });
    }
  }

  zoom(event, slide) {
    const { offsetX, offsetY } = event;
    const { offsetWidth, offsetHeight } = slide;

    const x = (offsetX / offsetWidth) * 100;
    const y = (offsetY / offsetHeight) * 100;

    this.getZoomImage(slide).style.transformOrigin = `${x}% ${y}%`;
    slide.setAttribute("data-zoom", "true");
  }

  getZoomImage(slide) {
    // The award badge can come before the product image in the slide
    return slide.querySelector(":scope > img");
  }

  resetZoom(slide) {
    this.getZoomImage(slide).style.transformOrigin = "center center";
    slide.setAttribute("data-zoom", "false");
    this.stateZoom = false;
  }
}

customElements.define("product-slideshow", ProductSlideshow);
